import React, { useState, useRef } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

const ImageUploader = ({ onCropped, currentImage, onRemoveImage }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({
    unit: '%',
    width: 80,
    height: 80,
    x: 10,
    y: 10,
    aspect: 16 / 9 // Proporción recomendada para imágenes de eventos
  });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [cropping, setCropping] = useState(false);
  const fileInputRef = useRef(null);
  const imgRef = useRef(null);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImageSrc(reader.result);
        setCropping(true);
      });
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const handleImageLoad = (img) => {
    // Ajustar el recorte inicial según la proporción de la imagen
    const { width, height } = img;
    const aspectRatio = width / height;
    
    let newCrop = {
      unit: '%',
      width: 80,
      height: 80 / aspectRatio,
      x: 10,
      y: 10,
      aspect: 16 / 9
    };

    if (aspectRatio > 16/9) {
      // Imagen más ancha que la proporción deseada
      newCrop.height = 80;
      newCrop.width = 80 * aspectRatio;
    }

    setCrop(newCrop);
  };

  const handleCropComplete = (crop) => {
    setCompletedCrop(crop);
  };

  const handleApplyCrop = () => {
    if (!completedCrop || !imgRef.current) return;

    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = completedCrop.width * scaleX;
    canvas.height = completedCrop.height * scaleY;

    const ctx = canvas.getContext('2d');

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );

    canvas.toBlob((blob) => {
      if (!blob) return;
      
      const croppedImage = new File(
        [blob], 
        'event-image.jpg', 
        { type: 'image/jpeg', lastModified: Date.now() }
      );
      
      onCropped(croppedImage);
      setCropping(false);
      setImageSrc(null);
    }, 'image/jpeg', 0.9);
  };

  const handleCancelCrop = () => {
    setCropping(false);
    setImageSrc(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveCurrentImage = () => {
    onRemoveImage();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-4">
      {cropping ? (
        <div className="space-y-4">
          <div className="max-h-96 overflow-auto bg-gray-100 p-2 rounded-lg">
            <ReactCrop
              src={imageSrc}
              crop={crop}
              onChange={c => setCrop(c)}
              onComplete={handleCropComplete}
              aspect={16 / 9}
              ruleOfThirds
              keepSelection
            >
              <img
                ref={imgRef}
                src={imageSrc}
                alt="Imagen a recortar"
                onLoad={(e) => handleImageLoad(e.currentTarget)}
                className="max-w-full"
              />
            </ReactCrop>
          </div>
          
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleApplyCrop}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Aplicar Recorte
            </button>
            <button
              type="button"
              onClick={handleCancelCrop}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <>
          {currentImage ? (
            <div className="relative group">
              <img
                src={currentImage}
                alt="Imagen actual del evento"
                className="w-full h-48 object-cover rounded-lg"
              />
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 rounded-lg">
                <button
                  type="button"
                  onClick={handleRemoveCurrentImage}
                  className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                >
                  Eliminar Imagen
                </button>
              </div>
            </div>
          ) : (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current.click()}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                Seleccionar Imagen
              </button>
              <p className="mt-2 text-sm text-gray-500">
                Formatos soportados: JPG, PNG. Tamaño recomendado: 16:9
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ImageUploader;